import { useQuoteDocumentEditable, useQuoteDocumentMode } from '@/components/quote-document/QuoteDocument';
import { SheetContinuationHeaders } from '@/components/quote-document/SheetLayoutContext';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import {
  isLightPageStyle,
  pageStyles,
  resolvePageStyleConfig,
  themeColors,
  uboTheme,
  type PageStyle,
} from '@/theme/uboTheme';
import { buildEditorSectionCss } from '@/theme/sectionStyles';
import PageHeader from '@/components/blocks/PageHeader';
import {
  useQuoteClientLogo,
  useQuotePageBrand,
  useResolvedAgencyLogoUrl,
} from '@/store/quoteClientLogoContext';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import { propagateClientLogo } from './logoSync';
import BrandingImage from '@/components/BrandingImage';
import { Palette } from 'lucide-react';

const STYLE_OPTIONS: { key: PageStyle; label: string; color: string; border?: string }[] = [
  { key: 'default', label: 'Wit', color: '#ffffff', border: '#d1d5db' },
  { key: 'juridisch', label: 'Juridisch', color: '#ffffff', border: '#050ced' },
  { key: 'primary', label: 'Blauw', color: '#050ced' },
  { key: 'positionering', label: 'Positionering', color: '#050ced' },
  { key: 'merkidentiteit', label: 'Merkidentiteit', color: '#050ced' },
  { key: 'strategie', label: 'Strategie', color: '#050ced' },
  { key: 'ontwikkelfase', label: 'Ontwikkelfase', color: '#050ced' },
];

function StylePicker({
  current,
  onChange,
  pageBrand,
}: {
  current: PageStyle;
  onChange: (s: PageStyle) => void;
  pageBrand: 'ubo' | 'ubx';
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [open]);

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium bg-black/20 hover:bg-black/30 text-white backdrop-blur-sm transition-colors"
        style={
          isLightPageStyle(current) ? { background: 'rgba(0,0,0,0.06)', color: '#374151' } : undefined
        }
      >
        <Palette className="h-3 w-3" />
        {pageStyles[current]?.label || 'Stijl'}
      </button>
      {open &&
        createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 p-1.5 min-w-[160px]"
            data-workspace-controls
            style={{ top: pos.top, left: pos.left }}
          >
            {STYLE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                  onClick={() => {
                    onChange(opt.key);
                    setOpen(false);
                  }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] text-gray-700 transition-colors ${
                  current === opt.key ? 'bg-primary/10 font-semibold' : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className="w-4 h-4 rounded-full shrink-0"
                  style={{
                    backgroundColor: resolvePageStyleConfig(opt.key, pageBrand).bgColor,
                    border: opt.border ? `2px solid ${opt.border}` : '2px solid transparent',
                  }}
                />
                {opt.label}
              </button>
            ))}
          </div>
        </>,
          document.body
      )}
    </div>
  );
}

export default function PageSectionView({ node, editor, updateAttributes }: NodeViewProps) {
  const isEditable = useQuoteDocumentMode() === 'edit';
  const canEditText = useQuoteDocumentEditable();
  const attrs = node.attrs as Record<string, unknown>;
  const sectionStyle = (attrs.sectionStyle as PageStyle) || 'default';
  const quoteBrand = useQuotePageBrand();
  const sectionTheme = attrs.theme as 'ubo' | 'ubx' | undefined;
  const pageBrand = sectionTheme === 'ubx' || sectionTheme === 'ubo' ? sectionTheme : quoteBrand;
  const styleConfig = resolvePageStyleConfig(sectionStyle, pageBrand);
  const clientLogoUrl = attrs.clientLogoUrl as string | undefined;
  const quoteClientLogo = useQuoteClientLogo();
  const effectiveLogo = clientLogoUrl ?? quoteClientLogo;
  const agencyLogoUrl = useResolvedAgencyLogoUrl(
    attrs.agencyLogoUrl as string | undefined,
    sectionTheme === 'ubx' || sectionTheme === 'ubo' ? sectionTheme : undefined
  );
  const isPlan = node.type.name === 'planVanAanpakSection';

  const cssText = useMemo(
    () =>
      buildEditorSectionCss(
        `.section-${attrs.id}`,
        sectionStyle,
        styleConfig.textColor,
        themeColors(pageBrand).primary
      ),
    [attrs.id, sectionStyle, styleConfig.textColor, pageBrand]
  );

  const handleClientLogoChange = useCallback(
    (url: string) => {
    updateAttributes({ clientLogoUrl: url });
    propagateClientLogo(editor, node, url);
    },
    [editor, node, updateAttributes]
  );

  return (
    <NodeViewWrapper
      as="div"
      className={`section-node-view section-${attrs.id}`}
      data-block-id={attrs.id}
      style={{ backgroundColor: styleConfig.bgColor }}
    >
      <style>{cssText}</style>

      <div
        style={{
          backgroundColor: styleConfig.bgColor,
          color: styleConfig.textColor,
          padding: A4_LANDSCAPE.padding,
          // Tot onderaan het laatste vel (zie sheetPagination).
          minHeight: `var(--sheet-content-min-height, ${A4_LANDSCAPE.contentHeight}px)`,
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <div contentEditable={false} style={{ userSelect: 'none' }} data-sheet-skip>
          <PageHeader
            clientLogoUrl={effectiveLogo}
            agencyLogoUrl={agencyLogoUrl}
            brand={pageBrand}
            variant={isLightPageStyle(sectionStyle) ? 'light' : 'dark'}
            isEditable={isEditable}
            onClientLogoChange={handleClientLogoChange}
          />

          {isEditable ? (
            <div className="absolute z-20" style={{ top: 12, right: 12 }}>
              <StylePicker
                current={sectionStyle}
                onChange={(s) => updateAttributes({ sectionStyle: s })}
                pageBrand={pageBrand}
              />
            </div>
          ) : null}

          <BrandingImage style={sectionStyle} />
        </div>

        <SheetContinuationHeaders
          blockId={String(attrs.id)}
          clientLogoUrl={effectiveLogo}
          agencyLogoUrl={agencyLogoUrl}
          brand={pageBrand}
          variant={isLightPageStyle(sectionStyle) ? 'light' : 'dark'}
        />

        <div className="relative z-[5]" style={{ marginTop: 24 }} data-sheet-body>
          {isPlan && (
            <div contentEditable={false}>
              <h2 className="mb-6">
                {canEditText ? (
                  <input
                    value={String(attrs.title || '')}
                    onChange={(event) => updateAttributes({ title: event.target.value })}
                    placeholder="PLAN VAN AANPAK"
                    aria-label="Titel plan van aanpak"
                    className="w-full border-0 bg-transparent p-0 outline-none"
                    style={{
                      fontSize: 32,
                      fontWeight: 900,
                      lineHeight: 1.2,
                      textTransform: 'uppercase',
                      fontFamily: uboTheme.fonts.primaryFallback,
                      color: uboTheme.colors.primary,
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 900,
                      lineHeight: 1.2,
                      textTransform: 'uppercase',
                      fontFamily: uboTheme.fonts.primaryFallback,
                      color: uboTheme.colors.primary,
                    }}
                  >
                    {String(attrs.title || 'Plan van Aanpak')}
                  </span>
                )}
              </h2>

              {attrs.showCompanyData !== false && attrs.companyName ? (
                // Invoervelden staan in dezelfde alinea's als de klant ziet: gelijke hoogte, gelijke vellen.
                <div className="mb-6 flex flex-col items-start">
                  <p className="w-full font-bold">
                    {canEditText ? (
                      <input
                        value={String(attrs.companyName || '')}
                        onChange={(event) => updateAttributes({ companyName: event.target.value })}
                        aria-label="Bedrijfsnaam"
                        className="block w-full border-0 bg-transparent p-0 outline-none"
                      />
                    ) : (
                      String(attrs.companyName || '')
                    )}
                  </p>
                  {attrs.companyAddress ? (
                    <p className="w-full text-sm">
                      {canEditText ? (
                        <input
                          value={String(attrs.companyAddress)}
                          onChange={(event) =>
                            updateAttributes({ companyAddress: event.target.value })
                          }
                          aria-label="Bedrijfsadres"
                          className="block w-full border-0 bg-transparent p-0 outline-none"
                        />
                      ) : (
                        String(attrs.companyAddress)
                      )}
                    </p>
                  ) : null}
                  {attrs.companyZip || attrs.companyCity ? (
                    <div className="flex w-full gap-1 text-sm">
                      {canEditText ? (
                        <p className="flex min-w-0 flex-1 gap-1">
                          <input
                            value={String(attrs.companyZip || '')}
                            onChange={(event) =>
                              updateAttributes({ companyZip: event.target.value })
                            }
                            aria-label="Postcode"
                            className="w-[8ch] border-0 bg-transparent p-0 outline-none"
                          />
                          <input
                            value={String(attrs.companyCity || '')}
                            onChange={(event) =>
                              updateAttributes({ companyCity: event.target.value })
                            }
                            aria-label="Plaats"
                            className="min-w-0 flex-1 border-0 bg-transparent p-0 outline-none"
                          />
                        </p>
                      ) : (
                        <p>
                          {`${String(attrs.companyZip || '')} ${String(attrs.companyCity || '')}`.trim()}
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  );
}
